import { createSlice, PayloadAction, createSelector } from '@reduxjs/toolkit';
import { RootState } from 'store/store';

import { UserState } from '../types';
import { userState } from './userState';

export const userSlice = createSlice({
  name: 'user',
  initialState: userState,
  reducers: {
    setUsers: (state, action: PayloadAction<UserState['users']>) => {
      state.users = action.payload;
    },
    setUserRoles: (state, action: PayloadAction<UserState['userRoles']>) => {
      state.userRoles = action.payload;
    },
    setTerminals: (state, action: PayloadAction<UserState['terminals']>) => {
      state.terminals = action.payload;
    },
    setUserPreferences: (
      state,
      action: PayloadAction<UserState['userPreferences']>,
    ) => {
      state.userPreferences = action.payload;
    },
    setVisibilitySets: (
      state,
      action: PayloadAction<UserState['visibilitySets']>,
    ) => {
      state.visibilitySets = action.payload;
    },
    setDriver: (state, action: PayloadAction<UserState['driver']>) => {
      state.driver = action.payload;
    },
    setHomeTerminalId: (
      state,
      action: PayloadAction<UserState['homeTerminalId']>,
    ) => {
      state.homeTerminalId = action.payload;
    },
    setLimit: (state, action: PayloadAction<UserState['limit']>) => {
      state.limit = action.payload;
    },
    setPageIndex: (state, action: PayloadAction<UserState['pageIndex']>) => {
      state.pageIndex = action.payload;
    },
    setTotalRecordCount: (
      state,
      action: PayloadAction<UserState['totalRecordCount']>,
    ) => {
      state.totalRecordCount = action.payload;
    },
    setSortBy: (state, action: PayloadAction<UserState['sortBy']>) => {
      state.sortBy = action.payload;
    },
    setSortField: (state, action: PayloadAction<UserState['sortField']>) => {
      state.sortField = action.payload;
    },
    setRecordId: (state, action: PayloadAction<UserState['recordId']>) => {
      state.recordId = action.payload;
    },
    setStatus: (state, action: PayloadAction<UserState['status']>) => {
      state.status = action.payload;
    },
    setSortAuthTerminals: (
      state,
      action: PayloadAction<UserState['sortAuthTerminals']>,
    ) => {
      state.sortAuthTerminals =
        action.payload as UserState['sortAuthTerminals'];
    },
    setAddEditModal: (
      state,
      action: PayloadAction<UserState['addEditModal']>,
    ) => {
      state.addEditModal = action.payload;
    },
    setExportModal: (state, action: PayloadAction<boolean>) => {
      state.exportModal = action.payload;
    },
    setResetData: () => {
      return userState;
    },
  },
});

export const { actions: userActions } = userSlice;

export const selectUser = (state: RootState) => state.user;

export const selectUsers = createSelector(selectUser, state => state.users);

export const selectUserRoles = createSelector(
  selectUser,
  state => state.userRoles,
);

export const selectTerminals = createSelector(
  selectUser,
  state => state.terminals,
);

export const selectUserPreferences = createSelector(
  selectUser,
  state => state.userPreferences,
);

export const selectVisibilitySets = createSelector(
  selectUser,
  (state: UserState) => state.visibilitySets,
);

export const selectDriver = createSelector(selectUser, state => state.driver);

export const selectHomeTerminalId = createSelector(
  selectUser,
  state => state.homeTerminalId,
);

export const selectLimit = createSelector(selectUser, state => state.limit);

export const selectPageIndex = createSelector(
  selectUser,
  state => state.pageIndex,
);

export const selectTotalRecordCount = createSelector(
  selectUser,
  state => state.totalRecordCount,
);

export const selectSortBy = createSelector(selectUser, state => state.sortBy);

export const selectSortField = createSelector(
  selectUser,
  state => state.sortField,
);

export const selectRecordId = createSelector(
  selectUser,
  state => state.recordId,
);

export const selectStatus = createSelector(selectUser, state => state.status);

export const selectSortAuthTerminals = createSelector(
  selectUser,
  state => state.sortAuthTerminals,
);

export const selectAddEditModal = createSelector(
  selectUser,
  state => state.addEditModal,
);

export const selectExportModal = createSelector(
  selectUser,
  state => state.exportModal,
);

export default userSlice.reducer;
